import { useToggle } from 'react-use'
import type { MouseEventHandler, ReactNode } from 'react'
import React, { useEffect } from 'react'
import { ShowIf, CollapseControl } from '@ztechapps/webui.util'

type ExtendingRowProps = {
  extensionRow: ReactNode
  colSpan?: number
  children: ReactNode
  _config?: Record<any, any>
  _id?: string
  showDefault?: boolean
  showCollapseControl?: boolean
  className?: string
  rowClassName?: string
  onMouseOver?: MouseEventHandler<HTMLTableRowElement>
  onMouseLeave?: MouseEventHandler<HTMLTableRowElement>
  onDoubleClick?: MouseEventHandler<HTMLTableRowElement>
  toggleTitle?: string //the accessible name of the toggle, which holds only an icon
  onToggleExtend?: (isExtend: boolean) => void //a callback that is called when the collapse control was toggled or clicked
  extensionRowClassName?: string
} & Record<any, any>

export const ExtendingRow = ({
  extensionRow,
  colSpan = 100,
  children,
  _config,
  _id,
  showDefault = false,
  showCollapseControl = true,
  className = '',
  rowClassName,
  onMouseLeave,
  onMouseOver,
  onDoubleClick,
  toggleTitle,
  onToggleExtend,
  extensionRowClassName,
  ...rest
}: ExtendingRowProps) => {
  const [show, setShow] = useToggle(showDefault)

  // useToggle seeds `show` only at mount, so a row whose showDefault flips true AFTER mount
  // (e.g. a same-page deep link updating ?userId= to select this row) would stay collapsed.
  // Sync the toggle open when that happens. Only ever lifts to open — never auto-collapses —
  // so consumers with a static showDefault are unaffected.
  useEffect(() => {
    if (showDefault) setShow(true)
  }, [showDefault, setShow])

  const { onSelect, highlightClassName, dragProps, dragRowClassName } = _config ?? {}

  return (
    <React.Fragment>
      <tr
        {...dragProps}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        onDoubleClick={onDoubleClick}
        className={`${highlightClassName ?? ''} ${rowClassName ?? ''} ${dragRowClassName ?? ''}`}
        onClick={() => {
          onSelect?.({ _id, _config, ...rest })
        }}
      >
        <td data-joyridestep="collapse control" style={{ width: '20px' }} className="px-1">
          <ShowIf condition={showCollapseControl}>
            <CollapseControl {...{ setShow, show }} title={toggleTitle} onToggleCollapse={onToggleExtend} />
          </ShowIf>
        </td>
        {children}
      </tr>
      <ShowIf condition={show}>
        <tr className={extensionRowClassName ?? ''}>
          <td colSpan={colSpan} className={`${className}`}>
            {extensionRow}
          </td>
        </tr>
      </ShowIf>
    </React.Fragment>
  )
}
